import { useState } from 'react'
import { ChevronDown, Folder, Plus } from 'lucide-react'
import { cn } from '../lib/cn'
import { Badge } from '../primitives/Badge'
import { FileTypeIcon } from '../primitives/FileTypeIcon'
import { SearchInput } from '../primitives/SearchInput'
import { Toggle } from '../primitives/Toggle'

export interface DocPool {
  id: string
  name: string
  documentCount?: number
  status?: 'ready' | 'indexing'
  progress?: number
}

export interface DocFile {
  id: string
  name: string
  sections?: number
}

export interface DocumentsViewProps {
  pools: DocPool[]
  filesByPool: Record<string, DocFile[]>
  isActive: (id: string) => boolean
  openDocName?: string | null
  query: string
  onQuery: (value: string) => void
  onToggleActive: (id: string) => void
  onAddDocument: (poolId: string) => void
  onCreatePool: () => void
}

export function DocumentsView({
  pools,
  filesByPool,
  isActive,
  openDocName,
  query,
  onQuery,
  onToggleActive,
  onAddDocument,
  onCreatePool,
}: DocumentsViewProps) {
  const [expanded, setExpanded] = useState<Set<string> | null>(null)
  const firstId = pools[0]?.id
  const expandedSet = expanded ?? new Set(firstId ? [firstId] : [])
  const toggleExpand = (id: string) =>
    setExpanded(() => {
      const next = new Set(expandedSet)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
      <p className="text-sm text-neutral-500">
        Dokumenten Pools bündeln Referenzen und sind per Vektorsuche durchsuchbar.
      </p>

      <SearchInput
        value={query}
        onChange={onQuery}
        placeholder="Semantische Suche über Dokumenten Pools…"
        right={<Badge tone="brand">Vektor</Badge>}
      />

      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Dokumenten Pools
        </span>
        <span className="text-xs text-neutral-400">Im Kontext suchen</span>
      </div>

      <div className="space-y-2.5">
        {pools.map((pool) => (
          <PoolCard
            key={pool.id}
            pool={pool}
            files={filesByPool[pool.id] ?? []}
            active={isActive(pool.id)}
            expanded={expandedSet.has(pool.id)}
            openDocName={openDocName}
            onToggleActive={() => onToggleActive(pool.id)}
            onToggleExpand={() => toggleExpand(pool.id)}
            onAddDocument={() => onAddDocument(pool.id)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onCreatePool}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-300 py-3 text-sm font-medium text-brand-600 hover:bg-brand-50/50"
      >
        <Plus className="h-4 w-4" /> Dokumenten Pool erstellen
      </button>
    </div>
  )
}

interface PoolCardProps {
  pool: DocPool
  files: DocFile[]
  active: boolean
  expanded: boolean
  openDocName?: string | null
  onToggleActive: () => void
  onToggleExpand: () => void
  onAddDocument: () => void
}

function PoolCard({
  pool,
  files,
  active,
  expanded,
  openDocName,
  onToggleActive,
  onToggleExpand,
  onAddDocument,
}: PoolCardProps) {
  const indexing = pool.status === 'indexing'
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border',
        active ? 'border-brand-200 bg-brand-50/30' : 'border-neutral-200',
      )}
    >
      <div className="flex items-center gap-3 p-3.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-neutral-100 text-neutral-500">
          <Folder className="h-4 w-4" />
        </span>
        <button type="button" onClick={onToggleExpand} className="min-w-0 flex-1 text-left">
          <div className="font-bold text-neutral-900">{pool.name}</div>
          <div className="text-xs text-neutral-500">
            <span
              className={cn(
                'mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle',
                indexing ? 'bg-pending' : 'bg-ok',
              )}
            />
            {pool.documentCount} Dokumente ·{' '}
            {indexing ? `wird indiziert · ${pool.progress}%` : 'indiziert'}
          </div>
        </button>
        <Toggle checked={active} onChange={onToggleActive} label={pool.name} />
        <button
          type="button"
          onClick={onToggleExpand}
          aria-label={expanded ? 'Einklappen' : 'Ausklappen'}
          className="text-neutral-400"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
        </button>
      </div>

      {indexing && (
        <div className="px-3.5 pb-2">
          <div className="h-1 w-full overflow-hidden rounded-full bg-neutral-200">
            <div
              className="h-full rounded-full bg-pending"
              style={{ width: `${pool.progress ?? 0}%` }}
            />
          </div>
        </div>
      )}

      {expanded && (
        <div className="border-t border-neutral-100">
          {files.map((file) => (
            <div key={file.id} className="flex items-center gap-2.5 px-3.5 py-2.5">
              <FileTypeIcon name={file.name} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-neutral-800">{file.name}</div>
                {file.sections != null && file.name !== openDocName && (
                  <div className="text-xs text-neutral-400">
                    {file.sections} Abschnitte indiziert
                  </div>
                )}
              </div>
              {file.name === openDocName && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-ok">
                  <span className="h-1.5 w-1.5 rounded-full bg-ok" /> geöffnet
                </span>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={onAddDocument}
            className="flex w-full items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium text-brand-600 hover:bg-neutral-50"
          >
            <Plus className="h-4 w-4" /> Dokument hinzufügen
          </button>
        </div>
      )}
    </div>
  )
}
